import { getRefundsKey } from 'api-hooks/refund/refund';
import {
  getRefundReleaseAgentKey,
  getRefundReleaseAgentsKey,
  RefundReleaseAgentModel,
} from 'api-hooks/refund/refund-release-agent';
import { useReleasingRefundReleaseAgent } from 'api-hooks/refund/refund-release-agent/mutation';
import { CheckboxCheckedSVG } from 'common/assets';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import { Form } from 'components/elements';
import { Button } from 'components/elements/buttons';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useDialog from 'hooks/use-dialog';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import { isDataActive } from 'hooks/use-void-action';
import TitleStatus from 'legacy-modules/common/title-status';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import AgentDetailField from './agent-detail-field';
import InformationField from './information-field';
import PaymentField from './payment-field';
import { FormContent, FormDivider } from './styles';

interface Props {
  refundReleaseAgent?: RefundReleaseAgentModel;
}

export default function RefundReleaseAgentForm(props: Props) {
  const { refundReleaseAgent } = props;

  const dialog = useDialog();
  const { t } = useTranslation();

  const { mutateAsync, isLoading } = useReleasingRefundReleaseAgent();

  const defaultValues = React.useMemo(() => {
    return {
      agentId: refundReleaseAgent?.agent.id,
      reason: refundReleaseAgent?.reason,
      orderNumber: refundReleaseAgent?.purchase.number,
      paymentMethodId: refundReleaseAgent?.paymentMethod.id,
      paymentName: refundReleaseAgent?.paymentMethod.type,
      refundAmount: refundReleaseAgent?.amount,
      requestedAt: refundReleaseAgent?.requestedAt,
      paymentableId: refundReleaseAgent?.paymentable?.chequeGiro?.id
        ? refundReleaseAgent.paymentable.chequeGiro.id
        : refundReleaseAgent?.paymentable.id,
      purchase: {
        transactionAt: refundReleaseAgent?.purchase.transactionAt,
        total: refundReleaseAgent?.purchase.total,
        agentId: refundReleaseAgent?.agent.id,
        paymentChannelId: refundReleaseAgent?.purchase.paymentChannel?.id,
        transactionType: refundReleaseAgent?.purchase.transactionType,
        id: refundReleaseAgent?.purchase.id,
      },
      chequeNote: refundReleaseAgent?.paymentable.chequeGiro
        ? {
            referenceNumber: refundReleaseAgent.paymentable.referenceNumber,
            issuedAt: refundReleaseAgent.paymentable.issuedAt,
            expiredAt: refundReleaseAgent.paymentable.expiredAt,
            availableAt: refundReleaseAgent.paymentable.availableAt,
            bankAccountId: refundReleaseAgent.paymentable.bankAccount.id,
          }
        : null,
    };
  }, [refundReleaseAgent]);

  const methods = useForm({
    defaultValues,
  });

  const onClickReleaseRequest = React.useCallback(() => {
    dialog.showConfirmation({
      title: t('common:confirmation'),
      message: t('common:confirmation_text'),
      onNegativeAction: (dismiss) => dismiss(),
      onPositiveAction: async (dismiss) => {
        dismiss();
        try {
          await mutateAsync({ releasePurchaseId: refundReleaseAgent!.id });
          queryClient.refetchQueries([getRefundReleaseAgentsKey()[0]]);
          queryClient.refetchQueries(
            getRefundReleaseAgentKey({ id: refundReleaseAgent!.id }),
          );
          queryClient.refetchQueries([getRefundsKey()[0]]);
        } catch (error) {
          notification.error(error.message);
        }
      },
    });
  }, [dialog, mutateAsync, refundReleaseAgent, t]);

  const isActive = isDataActive(refundReleaseAgent!.status);
  const { requestApprovalButton } = useApprovalAction(
    'release-purchases',
    refundReleaseAgent,
  );
  const { resolverButton } = useResolveApproval(
    'release-purchases',
    refundReleaseAgent,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'release-purchases',
    refundReleaseAgent,
  );
  const onRenderRightComponent = React.useMemo(() => {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
        {revertDraftButton}
        {isActive && (
          <Button
            buttonType="primary"
            startEnhancer={(color, size) => (
              <CheckboxCheckedSVG width={size} height={size} color={color} />
            )}
            onClick={onClickReleaseRequest}
            isLoading={isLoading}
          >
            {t('refund:request_release')}
          </Button>
        )}
        {requestApprovalButton}
        {resolverButton}
      </div>
    );
  }, [
    isActive,
    isLoading,
    onClickReleaseRequest,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    t,
  ]);

  const customViewName =
    refundReleaseAgent &&
    [t('common:refund_release_agent'), refundReleaseAgent.number].join(' - ');

  return (
    <Form
      methods={methods}
      onSubmit={() => {}}
      defaultEditable={!refundReleaseAgent}
      customViewName={customViewName}
    >
      <FormHeader
        data={refundReleaseAgent}
        onDelete={() => {}}
        title={
          refundReleaseAgent ? (
            <TitleStatus
              type="release"
              title={customViewName ?? t('common:refund_release_agent')}
              status={refundReleaseAgent.status}
            />
          ) : (
            t('common:add_extra', { extra: t('common:refund_release_agent') })
          )
        }
        noSubmit
        noDelete
        noEdit
        onRenderRightComponent={onRenderRightComponent}
      />
      <FormContent>
        <div>
          <InformationField />
          <PaymentField />
          <AgentDetailField />
          <SeparatorVertical $margin={36} />
          <FormDivider />
        </div>
      </FormContent>
    </Form>
  );
}
